import {useCallback, useMemo, useRef, useState} from "react";

import type {Coordinate} from "ol/coordinate";
import type {EventsKey} from "ol/events";
import type Feature from "ol/Feature";
import {LineString, Polygon} from "ol/geom";
import type Draw from "ol/interaction/Draw";
import type {Options as DrawOptions} from "ol/interaction/Draw";
import VectorLayer from "ol/layer/Vector";
import MapBrowserEvent from "ol/MapBrowserEvent";
import {unByKey} from "ol/Observable";
import type {Pixel} from "ol/pixel";
import VectorSource from "ol/source/Vector";
import {getArea, getLength} from "ol/sphere";
import {Fill, Stroke, Style} from "ol/style";
import CircleStyle from "ol/style/Circle";

const DRAWING_STYLE = {
    fill: new Fill({color: "rgba(255, 255, 255, 0.2)"}),
    stroke: new Stroke({color: "#6366f1", width: 2, lineDash: [10, 10]}),
    image: new CircleStyle({
        radius: 7,
        fill: new Fill({color: "#6366f1"}),
    }),
};

const VECTOR_STYLE = {
    fill: new Fill({color: "rgba(255, 255, 255, 0.3)"}),
    stroke: new Stroke({color: "#6366f1", width: 3}),
    image: new CircleStyle({
        radius: 7,
        fill: new Fill({color: "#6366f1"}),
    }),
};

/**
 * Zuletzt bekannte Zeigerposition auf der Karte. Sie wird gemerkt, weil die Streckenanzeige
 * und das Zurücknehmen eines Punktes die Position auch außerhalb eines pointermove brauchen.
 */
interface PointerMoveSnapshot {
    coordinate: Coordinate;
    pixel: Pixel;
    originalEvent: PointerEvent;
}

/** Messwerte, Zustand und Steuerfunktionen einer Flächenmessung. */
export interface AreaMeasurement {
    /** Fertig gemessene Fläche in Quadratmetern, sonst null. */
    measuredArea: number | null;
    /** Länge der ersten Kante in Metern, solange sie angezeigt wird, sonst null. */
    lineDistance: number | null;
    /** true, sobald die erste Kante durch den zweiten Punkt feststeht. */
    isFirstLineFixed: boolean;
    /** true im Freihand-Modus, in dem keine Strecke angezeigt wird. */
    isFreehandMode: boolean;
    /** Hängt die Mess-Logik an die Draw-Interaktion der Karte. */
    drawFunction: (drawInteraction: Draw) => void;
    /** Layer mit der gezeichneten Fläche, der Karte zu übergeben. */
    vectorLayer: VectorLayer<VectorSource>;
    /** Optionen für die Draw-Interaktion der Karte. */
    interactionOptions: DrawOptions;
    /** Bricht Zeichnung bzw. Messung ab; false, wenn es nichts abzubrechen gab. */
    cancelDrawing: () => boolean;
    /** Nimmt den zuletzt gesetzten Punkt zurück. */
    removeLastPoint: () => void;
    /** Schließt das Polygon, wenn genug Punkte gesetzt sind; false, wenn nicht gezeichnet wird. */
    finishDrawing: () => boolean;
    /** Setzt Messung und Zeichnung komplett zurück. */
    reset: () => void;
}

const formatArea = (polygon: Polygon) => {
    const area = getArea(polygon);
    return Math.round(area * 100) / 100;
};

/**
 * Kapselt die OpenLayers-Zeichenlogik zum Messen einer Fläche/Strecke auf der Karte
 * (Polygon zeichnen, Fläche berechnen, Freihand-Modus, Streckenanzeige während des
 * Zeichnens). Enthält bewusst KEIN UI (kein Dialog, kein Toast, keine Tastatur-Bindung) --
 * das bleibt Sache der aufrufenden Komponente, damit sich eine dialogbasierte und eine
 * inline Variante dieselbe Mess-Logik teilen können, statt sie zu duplizieren.
 */
export function useAreaMeasurement(): AreaMeasurement {
    const [measuredArea, setMeasuredArea] = useState<number | null>(null);
    const [lineDistance, setLineDistance] = useState<number | null>(null);
    const [isFirstLineFixed, setIsFirstLineFixed] = useState(false);
    const [isFreehandMode, setIsFreehandMode] = useState(false);

    const sourceRef = useRef(new VectorSource());
    const sketchRef = useRef<Feature | null>(null);
    const listenerRef = useRef<EventsKey | null>(null);
    const geomRef = useRef<Polygon | null>(null);
    const drawInteractionRef = useRef<Draw | null>(null);
    const lastPointerMoveEvtRef = useRef<PointerMoveSnapshot | null>(null);
    const firstPointRef = useRef<Coordinate | null>(null);
    const pointCountRef = useRef(0);
    const isFirstLineFixedRef = useRef(false);
    const isFreehandModeRef = useRef(false);

    const drawFunction = useCallback((drawInteraction: Draw) => {
        drawInteraction.on("drawstart", (evt) => {
            sourceRef.current.clear();
            setMeasuredArea(null);
            setLineDistance(null);
            setIsFirstLineFixed(false);
            setIsFreehandMode(false);
            firstPointRef.current = null;
            pointCountRef.current = 0;
            isFirstLineFixedRef.current = false;
            isFreehandModeRef.current = false;

            // Check if freehand mode (SHIFT key pressed) at draw start
            const lastEvt = lastPointerMoveEvtRef.current;
            if (lastEvt?.originalEvent.shiftKey) {
                isFreehandModeRef.current = true;
                setIsFreehandMode(true);
            }

            sketchRef.current = evt.feature;
            drawInteractionRef.current = drawInteraction;

            const sketchGeometry = evt.feature.getGeometry();
            if (sketchGeometry) {
                listenerRef.current = sketchGeometry.on("change", (changeEvt) => {
                    // Skip everything in freehand mode
                    if (isFreehandModeRef.current) {
                        if (changeEvt.target instanceof Polygon) {
                            geomRef.current = changeEvt.target;
                        }
                        return;
                    }

                    if (changeEvt.target instanceof Polygon) {
                        geomRef.current = changeEvt.target;

                        const coords = changeEvt.target.getCoordinates()[0];
                        const clickedPointCount = coords.length - 2;

                        // Detect freehand mode: if many points are added rapidly
                        if (clickedPointCount > 3 && pointCountRef.current <= 1) {
                            isFreehandModeRef.current = true;
                            setIsFreehandMode(true);
                            setLineDistance(null);
                            setIsFirstLineFixed(false);
                            isFirstLineFixedRef.current = false;
                            return;
                        }

                        // Update based on clicked point count
                        if (clickedPointCount >= 1) {
                            // Always update firstPointRef to current first point from geometry
                            firstPointRef.current = coords[0];

                            if (clickedPointCount === 1) {
                                // Only one point, show current mouse distance
                                if (pointCountRef.current >= 2) {
                                    setIsFirstLineFixed(false);
                                    isFirstLineFixedRef.current = false;
                                }
                                // Recalculate distance to current mouse position
                                const lastEvt = lastPointerMoveEvtRef.current;
                                if (lastEvt) {
                                    const firstLine = new LineString([coords[0], lastEvt.coordinate]);
                                    const distance = getLength(firstLine);
                                    setLineDistance(Math.round(distance * 100) / 100);
                                }
                                pointCountRef.current = 1;
                            } else if (clickedPointCount === 2) {
                                // Two points, show the fixed first line distance
                                if (!isFirstLineFixedRef.current) {
                                    const firstLine = new LineString([coords[0], coords[1]]);
                                    const distance = getLength(firstLine);
                                    setLineDistance(Math.round(distance * 100) / 100);
                                    setIsFirstLineFixed(true);
                                    isFirstLineFixedRef.current = true;
                                }
                                pointCountRef.current = 2;
                            } else if (clickedPointCount >= 3) {
                                // Third point or more, disable line distance display
                                setLineDistance(null);
                                setIsFirstLineFixed(false);
                                isFirstLineFixedRef.current = false;
                                pointCountRef.current = clickedPointCount;
                            }
                        }
                    }
                });
            }

            const map = drawInteraction.getMap();
            if (map) {
                // TODO: this listener is never unregistered, so every drawstart adds another one.
                map.on("pointermove", (e) => {
                    lastPointerMoveEvtRef.current = {
                        coordinate: e.coordinate,
                        pixel: e.pixel,
                        // A map pointermove always carries a PointerEvent; the union in the OL
                        // type only comes from the default type parameter of MapBrowserEvent.
                        originalEvent: e.originalEvent as PointerEvent,
                    };

                    // Update line distance while drawing if only one point
                    if (
                        firstPointRef.current &&
                        pointCountRef.current === 1 &&
                        !isFirstLineFixedRef.current &&
                        !isFreehandModeRef.current
                    ) {
                        const firstLine = new LineString([firstPointRef.current, e.coordinate]);
                        const distance = getLength(firstLine);
                        setLineDistance(Math.round(distance * 100) / 100);
                    }
                });
            }
        });

        drawInteraction.on("drawend", () => {
            if (geomRef.current) {
                const area = formatArea(geomRef.current);
                setMeasuredArea(area);
            }

            // Clear listener and refs for next drawing
            if (listenerRef.current) {
                unByKey(listenerRef.current);
                listenerRef.current = null;
            }

            lastPointerMoveEvtRef.current = null;
            geomRef.current = null;
            drawInteractionRef.current = null;
            firstPointRef.current = null;
            pointCountRef.current = 0;
            isFirstLineFixedRef.current = false;
            isFreehandModeRef.current = false;
            setLineDistance(null);
            setIsFirstLineFixed(false);
            setIsFreehandMode(false);
        });
    }, []);

    /**
     * Bricht die aktuelle Zeichnung/Messung ab: erst eine laufende Zeichnung, sonst eine
     * bereits gemessene Fläche. Gibt zurück, ob es etwas zum Abbrechen gab -- `false`
     * bedeutet "nichts zu tun", der Aufrufer kann daraus schließen, dass der Nutzer den
     * Mess-Modus komplett verlassen möchte.
     */
    const cancelDrawing = useCallback(() => {
        const cleanupDrawingState = () => {
            if (sketchRef.current) {
                sourceRef.current.removeFeature(sketchRef.current);
                sketchRef.current = null;
            }
            if (listenerRef.current) {
                unByKey(listenerRef.current);
                listenerRef.current = null;
            }
            geomRef.current = null;
            drawInteractionRef.current = null;
            lastPointerMoveEvtRef.current = null;
            firstPointRef.current = null;
            pointCountRef.current = 0;
            isFirstLineFixedRef.current = false;
            isFreehandModeRef.current = false;
            sourceRef.current.clear();
            setMeasuredArea(null);
            setLineDistance(null);
            setIsFirstLineFixed(false);
            setIsFreehandMode(false);
        };

        if (drawInteractionRef.current) {
            drawInteractionRef.current.abortDrawing();
            cleanupDrawingState();
            return true;
        }

        if (sketchRef.current || measuredArea) {
            cleanupDrawingState();
            return true;
        }

        return false;
    }, [measuredArea]);

    const removeLastPoint = useCallback(() => {
        if (drawInteractionRef.current) {
            const draw = drawInteractionRef.current;

            // Check current point count before removing
            const prevPointCount = pointCountRef.current;

            draw.removeLastPoint();

            // If we went from 1 point to 0, drawing is aborted
            if (prevPointCount <= 1) {
                setLineDistance(null);
                setIsFirstLineFixed(false);
                isFirstLineFixedRef.current = false;
                firstPointRef.current = null;
                pointCountRef.current = 0;
            }

            const map = draw.getMap();
            const lastEvt = lastPointerMoveEvtRef.current;
            if (map && lastEvt) {
                const replayEvt = new MapBrowserEvent("pointermove", map, lastEvt.originalEvent, false);
                replayEvt.coordinate = lastEvt.coordinate;
                replayEvt.pixel = lastEvt.pixel;
                draw.handleEvent(replayEvt);
            }
        }

        if (sketchRef.current && !drawInteractionRef.current) {
            sourceRef.current.removeFeature(sketchRef.current);
            sketchRef.current = null;
            geomRef.current = null;
            sourceRef.current.clear();
            setMeasuredArea(null);
            setLineDistance(null);
            setIsFirstLineFixed(false);
            isFirstLineFixedRef.current = false;
            firstPointRef.current = null;
            pointCountRef.current = 0;
        }
    }, []);

    /**
     * Beendet das aktuell gezeichnete Polygon, wenn genug Punkte gesetzt sind (Pendant zum
     * Doppelklick, z.B. für eine Enter-Taste). Gibt zurück, ob eine laufende Zeichnung
     * beendet wurde.
     */
    const finishDrawing = useCallback(() => {
        const geometry = sketchRef.current?.getGeometry();
        if (drawInteractionRef.current && geometry instanceof Polygon) {
            const coords = geometry.getCoordinates()[0];
            if (coords.length >= 5) {
                drawInteractionRef.current.finishDrawing();
                return true;
            }
        }
        return false;
    }, []);

    /** Setzt Messung/Zeichnung komplett zurück (z.B. beim Verlassen des Mess-Modus). */
    const reset = useCallback(() => {
        setMeasuredArea(null);
        setLineDistance(null);
        setIsFirstLineFixed(false);
        setIsFreehandMode(false);
        sourceRef.current.clear();
        sketchRef.current = null;
        geomRef.current = null;
        drawInteractionRef.current = null;
        firstPointRef.current = null;
        pointCountRef.current = 0;
        isFirstLineFixedRef.current = false;
        isFreehandModeRef.current = false;
        if (listenerRef.current) {
            unByKey(listenerRef.current);
            listenerRef.current = null;
        }
    }, []);

    const vectorLayer = useMemo(
        () =>
            new VectorLayer({
                source: sourceRef.current,
                style: new Style(VECTOR_STYLE),
            }),
        [],
    );

    const interactionOptions = useMemo<DrawOptions>(
        () => ({
            source: sourceRef.current,
            type: "Polygon",
            style: new Style(DRAWING_STYLE),
        }),
        [],
    );

    return {
        measuredArea,
        lineDistance,
        isFirstLineFixed,
        isFreehandMode,
        drawFunction,
        vectorLayer,
        interactionOptions,
        cancelDrawing,
        removeLastPoint,
        finishDrawing,
        reset,
    };
}
